// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid2, IconButton, Stack, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import { BaseDatePicker, BaseLabel, BaseSelect, BaseTable, Loading, UnderDevelopment } from '~/components/common';
// Others
import { ISubRentalsReport, IQueryParamListExport } from '~/utils/interface/reports';
import {
  BACK_TO_PREVIOUS_PAGE,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  optionDateTypeSubRentalReport,
  optionStatusPO,
} from '~/utils/constants/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { IBaseOption, IHeaderCellType, IResultCallApiFn } from '~/utils/interface/common';
import { formatCurrencyUSD } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { exportDataReport, getListSubRentalsReport } from '~/thunks/reports/reportsThunk';
import { ReportTypeEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './ReportsSubrentalsReport.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<ISubRentalsReport>[] => {
  return [
    {
      id: 'vendor',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_subrental')}</Box>
        </Stack>
      ),
      render: (record: ISubRentalsReport) => (
        <Stack>
          <Typography className={cx('vendorName')}>{record?.vendor?.name}</Typography>
        </Stack>
      ),
    },
    {
      id: 'product',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_product')}</Box>
        </Stack>
      ),
      render: (record: ISubRentalsReport) => {
        return <Typography className={cx('textValue')}>{record?.product?.name}</Typography>;
      },
    },
    {
      id: 'quantity',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_quantity')}</Box>
        </Stack>
      ),
      render: (record: ISubRentalsReport) => {
        return <Typography className={cx('textQuantity')}>{record?.quantity}</Typography>;
      },
    },
    {
      id: 'price',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_price')}</Box>
        </Stack>
      ),
      render: (record: ISubRentalsReport) => {
        return (
          <Typography className={cx('textQuantity')}>
            {formatCurrencyUSD({ value: record?.price, showDecimal: true })}
          </Typography>
        );
      },
    },
    {
      id: 'subtotal',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_subtotal')}</Box>
        </Stack>
      ),
      render: (record: ISubRentalsReport) => {
        return (
          <Typography className={cx('textSubtotal')}>
            {formatCurrencyUSD({ value: record?.subtotal, showDecimal: true })}
          </Typography>
        );
      },
    },
  ];
};

const ReportsSubrentalsReport = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [subrentalList, setSubrentalList] = useState<ISubRentalsReport[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
  });
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListSubRentalsReport();
  }, [paramsObject.page]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListSubRentalsReport = () => {
    setIsLoading(true);

    dispatch(getListSubRentalsReport(paramsObject))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { responses, pagination } = res.data;

        setSubrentalList(responses);
        setPagination(pagination);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleApplyFilter = () => {
    handleGetListSubRentalsReport();
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: ReportTypeEnum.SUB_RENTAL,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamsObject({ ...paramsObject, page });
    setSearchParams({ ...params, page: String(page) });
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const fetchOptionDateType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionDateTypeSubRentalReport,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionPOStatus = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionStatusPO,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Handle Function

  return (
    <Stack id='reportsSubrentalsPage' className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
          <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>

        <Stack direction={'column'} spacing={2}>
          <BaseLabel label={t('reports_subrentals_title')} styleType='bigTitle' />
          <Typography className={cx('description')}>{t('reports_subrentals_desc_page')}</Typography>
        </Stack>
      </Stack>

      <Stack direction={'column'} gap={5} className={cx('viewAction')}>
        <Grid2 columnSpacing={'24px'} alignItems={'start'} rowSpacing={'24px'} container>
          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_start_date', { ns: 'common/base' })}
              value={paramsObject.startDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  startDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_end_date', { ns: 'common/base' })}
              value={paramsObject.endDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  endDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseSelect
              gap={8}
              height={40}
              styleType='contained'
              label={t('reports_filter_date_type')}
              onChange={(data) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  dateType: data?.value,
                }));
              }}
              callApiFn={fetchOptionDateType}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseSelect
              gap={8}
              height={40}
              styleType='contained'
              label={t('reports_filter_po_status')}
              onChange={(data) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  purchaseOrderStatus: data?.value,
                }));
              }}
              callApiFn={fetchOptionPOStatus}
            />
          </Grid2>
        </Grid2>

        <Stack direction='row' justifyContent={'space-between'} alignItems='center'>
          <Button
            type='button'
            variant='contained'
            className={cx('buttonApply')}
            onClick={handleApplyFilter}
            startIcon={<img src={icons.commonIconFilter} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_action_apply', { ns: 'common/base' })}
          </Button>

          <Button
            type='button'
            variant='contained'
            className={cx('buttonExport')}
            onClick={handleExport}
            disabled={!subrentalList.length}
            startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_button_export_excel', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>

      <Box className={cx('tableContainer')}>
        <BaseTable
          dataSource={subrentalList}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columns(t)}
          onPageChange={handlePageChange}
        />
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default ReportsSubrentalsReport;
